import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  BarChart3, 
  Users, 
  CheckCircle,
  ArrowRight,
  Mail,
  Award,
  Target,
  FileText,
  Globe,
  Star
} from "lucide-react";

const ResearchStudies = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Search,
      title: "Research Design",
      description: "Comprehensive research methodology development including study design, sampling strategies, and data collection frameworks.",
      features: ["Study Design", "Sampling Strategy", "Methodology Development", "Research Framework"]
    },
    {
      icon: BarChart3,
      title: "Data Collection",
      description: "Expert data collection services including both qualitative and quantitative research methods for comprehensive insights.",
      features: ["Survey Design", "Interview Conduct", "Focus Groups", "Field Research"]
    },
    {
      icon: Users,
      title: "Qualitative Research",
      description: "In-depth qualitative research including interviews, focus groups, and ethnographic studies for rich contextual insights.",
      features: ["In-depth Interviews", "Focus Group Discussions", "Ethnographic Studies", "Case Studies"]
    },
    {
      icon: BarChart3,
      title: "Quantitative Analysis",
      description: "Advanced statistical analysis and data processing to extract meaningful insights from quantitative research data.",
      features: ["Statistical Analysis", "Data Processing", "Regression Analysis", "Predictive Modeling"]
    },
    {
      icon: FileText,
      title: "Report Writing",
      description: "Professional research report preparation with clear findings, recommendations, and actionable insights.",
      features: ["Report Writing", "Data Visualization", "Executive Summaries", "Policy Briefs"]
    },
    {
      icon: Target,
      title: "Impact Assessment",
      description: "Comprehensive impact assessment studies to evaluate program effectiveness and measure social outcomes.",
      features: ["Impact Evaluation", "Outcome Measurement", "Baseline Studies", "Endline Assessment"]
    }
  ];

  const stats = [
    { number: "50+", label: "Research Studies", icon: Search },
    { number: "20+", label: "Sectors Covered", icon: Globe },
    { number: "95%", label: "Client Satisfaction", icon: Award },
    { number: "8+", label: "Years Experience", icon: Star }
  ];

  const process = [
    {
      step: "01",
      title: "Research Planning",
      description: "Comprehensive research planning including objective setting, methodology selection, and timeline development.",
      icon: Target
    },
    {
      step: "02",
      title: "Data Collection",
      description: "Systematic data collection using appropriate research methods and tools for comprehensive information gathering.",
      icon: Users
    },
    {
      step: "03",
      title: "Analysis & Processing",
      description: "Advanced data analysis and processing using statistical tools and qualitative analysis techniques.",
      icon: BarChart3
    },
    {
      step: "04",
      title: "Reporting & Dissemination",
      description: "Professional report preparation and dissemination of findings with actionable recommendations.",
      icon: FileText
    }
  ];

  const benefits = [
    "Comprehensive research expertise",
    "Mixed-methods approach",
    "Multi-sector experience",
    "Advanced analytical capabilities",
    "Professional reporting standards",
    "Evidence-based insights"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-violet-900 via-purple-800 to-indigo-900 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Geometric Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-24 h-24 bg-white transform rotate-45"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-white transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-1/3 w-12 h-12 bg-white transform rotate-45"></div>
        </div>
        
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold border border-white/30">
                  <Search className="h-5 w-5 mr-3" />
                  Research Excellence
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Expert <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-indigo-300">Research & Studies</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Expert research support including questionnaire design, data collection (qualitative & quantitative), analysis, and quality report preparation.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                 onClick={()=>navigate('/contactus')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-purple-400 to-indigo-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  Start Your Research
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
                <motion.button
                 onClick={()=>navigate('/projects')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-violet-900 transition-all duration-300"
                >
                  View Our Studies
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Our Research Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                        className="text-center"
                      >
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <stat.icon className="h-8 w-8 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-white">{stat.number}</div>
                        <div className="text-white/80 text-sm">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Proven <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">Research Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures rigorous research methodology and high-quality insights for informed decision-making.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-200 hover:border-violet-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center">
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-gray-200">{step.step}</div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-violet-300 to-purple-300 transform -translate-y-1/2"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">Research Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From research design to report writing, we provide end-to-end research solutions that deliver actionable insights and evidence-based recommendations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 h-full border border-gray-200 hover:border-violet-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-violet-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-violet-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">Research Services?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Partner with us for comprehensive research solutions that provide evidence-based insights and drive informed decision-making.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-violet-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-violet-600 to-purple-600 rounded-3xl p-8 text-white"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <Search className="h-16 w-16 mx-auto mb-4 text-white/80" />
                  <h3 className="text-2xl font-bold mb-2">Research Excellence</h3>
                  <p className="text-white/90">Your insights, our expertise</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h4 className="text-lg font-semibold mb-4">Start Your Research</h4>
                  <p className="text-white/90 mb-6">
                    Ready to gain valuable insights? Let's discuss how our research services can help you make informed decisions and drive positive change.
                  </p>
                  <motion.button
                    onClick={() => navigate('/contactus')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-white text-violet-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center"
                  >
                    Contact Us Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-violet-600 to-purple-600">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Gain <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Valuable Insights</span>?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
              Let's discuss your research needs and create comprehensive studies that provide actionable insights and evidence-based recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">

              <motion.button
               onClick={()=>navigate('/contactus')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-violet-600 transition-all duration-300 flex items-center justify-center"
              >
                <Mail className="mr-2 h-5 w-5" />
                Discuss Your Needs
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResearchStudies;